import { useTranslation } from "react-i18next";
import type { PointOfInterest, ThemeClasses } from "../types";
import { getStatusHumanLabel } from "../../../../../../utils/getStatusEntity";
import { useStatusesStore } from "../../../../../../store/admin/statusesStore";
import { ADMIN_RESELLER_ROLE_ID } from "../../../../../../hooks/useIsReseller";
import { useDisableCRUD } from "../../../../../../store/admin/companiesStore";
import { CRUDTooltip } from "../../../../../../components/base/CRUDTooltip";

interface PointOfInterestCardProps {
  poi: PointOfInterest;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onEdit: (poi: PointOfInterest) => void;
  onDetails: (poi: PointOfInterest) => void;
  onDelete?: (poi: PointOfInterest) => void;
}

const getCategoryIcon = (categoryId?: string): string => {
  switch (categoryId) {
    case "residence":
      return "ri-home-4-line";
    case "client":
      return "ri-user-line";
    case "work":
      return "ri-briefcase-line";
    case "warehouse":
      return "ri-store-2-line";
    case "office":
      return "ri-building-line";
    case "gas_station":
      return "ri-gas-station-line";
    default:
      return "ri-map-pin-line";
  }
};

const getCategoryLabel = (categoryId: string | undefined, t: (key: string) => string): string => {
  if (!categoryId) return t("pointsOfInterest.categories.other");
  return t(`pointsOfInterest.categories.${categoryId}`);
};

const getStatusColorByName = (statusName?: string): string => {
  const name = statusName?.toLowerCase() || "";
  if (name.includes("activ") && !name.includes("inactiv")) {
    return "text-primary-500 bg-primary-500/10";
  }
  if (name.includes("inactiv")) {
    return "text-red-500 bg-red-500/10";
  }
  return "text-gray-500 bg-gray-500/10";
};

const formatDate = (timestamp?: { _seconds: number; _nanoseconds: number }): string => {
  if (!timestamp) return "";
  const date = new Date(timestamp._seconds * 1000);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffHours < 1) return "Hace unos minutos";
  if (diffHours < 24) return `Hace ${diffHours} hora${diffHours > 1 ? "s" : ""}`;
  if (diffDays === 1) return "Hace 1 día";
  if (diffDays < 7) return `Hace ${diffDays} días`;
  if (diffDays < 30) return `Hace ${Math.floor(diffDays / 7)} semana${Math.floor(diffDays / 7) > 1 ? "s" : ""}`;
  
  return date.toLocaleDateString();
};

export function PointOfInterestCard({
  poi,
  themeClasses,
  isDarkMode,
  onEdit,
  onDetails,
  onDelete,
}: PointOfInterestCardProps) {
  const { t } = useTranslation("adminSections");
  const statuses = useStatusesStore((state) => state.statuses);
  const disableCRUD = useDisableCRUD();

  // Obtener información del usuario para permisos
  const userId = localStorage.getItem("userId") || "";
  const userRoleId = localStorage.getItem("userRoleId") || "";
  const isReseller = userRoleId === ADMIN_RESELLER_ROLE_ID;

  // Verificar si puede editar: reseller puede todo, carrier solo sus creaciones
  const canEdit = isReseller || poi.createdBy === userId;

  // Obtener el status del POI desde el store
  const poiStatus = statuses.find((s) => s.id === poi.status_id);
  const statusLabel = poiStatus?.name
    ? getStatusHumanLabel(poiStatus.name, t)
    : t("pointsOfInterest.status.unknown");
  
  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 flex flex-col gap-3 hover:shadow-md transition-shadow`}
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? "bg-dark-surface" : "bg-gray-100"}`}>
            <i className={`${getCategoryIcon(poi.category_id)} text-lg ${themeClasses.textSecondary}`}></i>
          </div>
          <div>
            <h3 className={`font-semibold ${themeClasses.text} text-sm`}>{poi.name}</h3>
            <p className={`text-xs ${themeClasses.textSecondary}`}>
              {getCategoryLabel(poi.category_id, t)}
            </p>
          </div>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColorByName(poiStatus?.name)}`}>
          {statusLabel}
        </span>
      </div>

      {/* Coordinates */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <p className={`text-xs ${themeClasses.textSecondary}`}>{t("pointsOfInterest.fields.latitude")}</p>
          <p className={`text-sm font-medium ${themeClasses.text}`}>
            {poi.location?.latitude?.toFixed(4) || "-"}
          </p>
        </div>
        <div>
          <p className={`text-xs ${themeClasses.textSecondary}`}>{t("pointsOfInterest.fields.longitude")}</p>
          <p className={`text-sm font-medium ${themeClasses.text}`}>
            {poi.location?.longitude?.toFixed(4) || "-"}
          </p>
        </div>
      </div>

      {/* Type */}
      <div className="flex items-center justify-between">
        <p className={`text-xs ${themeClasses.textSecondary}`}>{t("pointsOfInterest.fields.type")}</p>
        <span className={`text-xs font-medium ${poi.shared ? "text-blue-500" : "text-amber-500"}`}>
          {poi.shared ? t("pointsOfInterest.visibility.shared") : t("pointsOfInterest.visibility.private")}
        </span>
      </div>

      {/* Address */}
      <div>
        <p className={`text-xs ${themeClasses.textSecondary}`}>{t("pointsOfInterest.fields.address")}</p>
        <p className={`text-sm ${themeClasses.text} truncate`}>
          {poi.address || t("pointsOfInterest.noAddress")}
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-2 border-t border-gray-200 dark:border-dark-border">
        <button
          onClick={() => onDetails(poi)}
          className={`${canEdit ? 'flex-1' : 'w-full'} text-xs py-2 px-3 rounded-lg ${themeClasses.buttonHover} ${themeClasses.text} transition-colors`}
        >
          {t("pointsOfInterest.actions.details")}
        </button>
        {canEdit && (
          <CRUDTooltip>
            <button
              onClick={() => onEdit(poi)}
              disabled={disableCRUD}
              className="text-xs py-2 px-4 bg-primary-500 hover:bg-primary-400 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("pointsOfInterest.actions.edit")}
            </button>
          </CRUDTooltip>
        )}
        {canEdit && onDelete && (
          <button
            onClick={() => onDelete(poi)}
            className="text-xs py-2 px-3 bg-red-500/10 hover:bg-red-500/20 text-red-500 rounded-lg transition-colors"
            title={t("pointsOfInterest.actions.delete", "Eliminar")}
          >
            <i className="ri-delete-bin-line"></i>
          </button>
        )}
      </div>

      {/* Last update */}
      <p className={`text-xs ${themeClasses.textSecondary}`}>
        {t("pointsOfInterest.lastUpdate")}: {formatDate(poi.updatedAt)}
      </p>
    </div>
  );
}
